/**
 * The texts of dictation (ANN-16), comments on images (ANN-25), study reports (ANN-26), the check for
 * identifying words (ANN-28) and the dictation benchmark, spread into the app's dictionaries (`lib/i18n.tsx`).
 */
export const dictationEn = {
  dictate: 'Dictate',
  dictateHelp: 'Dictate: click to start and stop, or hold {key}',
  dictateHelpClick: 'Dictate: click to start and stop',
  dictationStarting: 'Opening the microphone…',
  dictationListening: 'Listening…',
  dictationFinishing: 'Writing…',
  dictationCut: 'The field is full: the end of the dictation was left out.',
  dictationError: 'Dictation failed ({code})',
  dictationError_microphone: 'The microphone may not be used: allow it for this site in the browser.',
  dictationError_noMicrophone: 'No microphone was found.',
  dictationError_microphoneBusy: 'The microphone is in use by another program.',
  dictationError_unsupported: 'This browser cannot dictate.',
  dictationError_noAudio: 'The browser starts no audio: it needs a sound device on this computer, such as speakers or a headset.',
  dictationError_connection: 'The connection to the speech service broke off. Try again.',
  dictationError_timeout: 'The speech service did not answer. Try again.',
  dictationError_unavailable: 'Dictation is unavailable right now.',
  dictationError_busy: 'Dictation is open in another tab or window: close it there, or wait two minutes.',
  dictationError_session: 'Your session has ended: log in again to dictate.',
  dictationError_forbidden: 'Only platform admins may record for the benchmark.',
  dictationError_tooLong: 'A phrase lasts at most two minutes: dictation stopped there.',
  dictationError_recording: 'The recording was not kept.',
  dictationError_recordingOff: 'Recording is not set up on the speech service.',
  dictatedWith: 'Dictated ({model})',
  identifyingFound: 'This may identify someone. Rewrite it, or confirm that it identifies no one:',
  identifyingConfirm: 'Identifies no one',
  identifyingSelect: 'Select in the text: {kind}',
  identifying_date: 'a date',
  identifying_number: 'a long number',
  identifying_name: 'a name',
  identifying_birth: 'a birth',
  imageComments: 'Comments',
  commentTool: 'Comment at a point',
  commentToolHelp: 'Click on the image where the comment belongs',
  commentOnImage: 'Comment on the image',
  commentWholeImage: 'whole image',
  commentAtPoint: 'at pin {number}',
  commentSave: 'Save comment',
  commentEdit: 'Edit',
  commentNone: 'No comments on this image.',
  commentLimit: 'At most {count} comments on an image.',
  change_commented: '{count} comments added',
  change_commented_one: 'a comment added',
  change_comment_changed: '{count} comments changed',
  change_comment_changed_one: 'a comment changed',
  change_uncommented: '{count} comments removed',
  change_uncommented_one: 'a comment removed',
  commentsHint: 'Your comments are part of your annotations: they are seen where these are.',
  studyReport: 'Report',
  studyReportHint: 'Your report on this study, one for all its views; it is saved as you write and seen where your annotations are.',
  studyReportEmpty: 'An empty report is removed.',
  studyReportChecking: 'Not saved until the marked words are rewritten or confirmed.',
  readerReport: 'Reader’s report',
  clinicalReports: 'Clinical report',
  clinicalReportsHint: 'The report this study received at its hospital, de-identified before upload. It is no reader’s report and nobody edits it.',
  clinicalReportViews: 'With {views}',
  clinicalReportCumulative: 'Covers several examinations',
  clinicalReportIndication: 'Indication',
  clinicalReportQuestion: 'Clinical question',
  clinicalReportFindings: 'Findings',
  clinicalReportConclusion: 'Conclusion',
  clinicalReportNoFindings: 'No findings section.',
  clinicalReportContextOnly: 'Your job shows the clinical context only: findings and conclusion follow once you finish your assignment.',
  deleteClinicalReport: 'Delete clinical report',
  deleteClinicalReportWarning: 'For example, when it identifies a patient. This cannot be undone.',
  dockLabel: 'Dictation and views',
  dockStudy: 'Study',
  dockReportDictate: 'Dictate the study report',
  dockReportAdd: 'Dictate more of the study report',
  dockReportScope: 'One report for all {count} images of the study',
  dockReportFinished: 'The report cannot change while the active view is finished',
  dockCommentDictate: 'Dictate a comment on {view}',
  dockCommentAdd: 'Dictate more of the comment on {view}',
  dockCommentScope: 'This image only: its comment on the whole image, saved in the side panel',
  dockFinished: '{view} is finished in this job',
  dockHold: 'Click, or hold {key}',
  dockArm: 'Click; {key} then talks here',
  dockClick: 'Click to start and stop',
  dockListening: 'Listening: click to stop, Esc drops the phrase',
  dockViews: 'Images {from}–{to} of {total} shown',
  dockView: 'Image {from} of {total} shown',
  dockFinishFirst: 'Finish dictating the comment first',
  dockAddedReport: 'Added to the study report.',
  dockAddedComment: 'Added to the comment on {view}: save it in the side panel.',
  dockDropped: 'Its field was closed, so the words were dropped.',
  speechBench: 'Dictation benchmark',
  speechBenchIntro:
    'Read the invented texts aloud, one by one, as you would dictate them. Each one is recorded with what the live dictation made of it, for the speech benchmark. Read only these texts, never anything about a real patient.',
  speechBenchRecordingOff: 'Recording is not set up on the speech service: you can try the texts, but nothing is kept.',
  speechSpeaker: 'Speaker code',
  speechSpeakerHint: 'Letters and digits, up to 16, such as A1. Never a name.',
  speechTextNumber: 'Text {number} of {total}',
  speechText: 'Text',
  speechTerms: 'Terms',
  speechRecord: 'Record',
  speechTry: 'Try',
  speechStop: 'Stop',
  speechRecordHelp: 'Click to start and stop, or hold {key}',
  speechLive: 'Live',
  speechFinal: 'Final',
  speechTimes: '{seconds} s spoken, final text {final} s after release',
  speechCorrections: 'Glossary corrections',
  speechEngine: 'Speech model',
  speechFinalPass: 'Final pass',
  speechGlossary: 'Glossary',
  speechRecordings: 'Recordings',
  speechRecordingsNone: 'No recordings yet.',
  speechRecorded: 'Recorded',
  speechSeconds: 'Seconds',
  speechRecordedAlready: 'recorded by this speaker',
}

export const dictationDe: Record<keyof typeof dictationEn, string> = {
  dictate: 'Diktieren',
  dictateHelp: 'Diktieren: Klick startet und beendet, oder {key} halten',
  dictateHelpClick: 'Diktieren: Klick startet und beendet',
  dictationStarting: 'Mikrofon wird geöffnet …',
  dictationListening: 'Hört zu …',
  dictationFinishing: 'Schreibt …',
  dictationCut: 'Das Feld ist voll: Das Ende des Diktats wurde weggelassen.',
  dictationError: 'Diktieren fehlgeschlagen ({code})',
  dictationError_microphone: 'Das Mikrofon darf nicht verwendet werden: Erlauben Sie es für diese Seite im Browser.',
  dictationError_noMicrophone: 'Kein Mikrofon gefunden.',
  dictationError_microphoneBusy: 'Das Mikrofon wird von einem anderen Programm verwendet.',
  dictationError_unsupported: 'Dieser Browser kann nicht diktieren.',
  dictationError_noAudio: 'Der Browser startet kein Audio: Er braucht ein Audiogerät an diesem Computer, etwa Lautsprecher oder ein Headset.',
  dictationError_connection: 'Die Verbindung zum Sprachdienst ist abgebrochen. Versuchen Sie es erneut.',
  dictationError_timeout: 'Der Sprachdienst hat nicht geantwortet. Versuchen Sie es erneut.',
  dictationError_unavailable: 'Diktieren ist gerade nicht verfügbar.',
  dictationError_busy: 'Diktieren ist in einem anderen Tab oder Fenster offen: Schließen Sie es dort oder warten Sie zwei Minuten.',
  dictationError_session: 'Ihre Sitzung ist abgelaufen: Melden Sie sich erneut an, um zu diktieren.',
  dictationError_forbidden: 'Nur Plattform-Admins dürfen für den Benchmark aufnehmen.',
  dictationError_tooLong: 'Eine Phrase dauert höchstens zwei Minuten: Das Diktat wurde dort beendet.',
  dictationError_recording: 'Die Aufnahme wurde nicht behalten.',
  dictationError_recordingOff: 'Aufnahmen sind im Sprachdienst nicht eingerichtet.',
  dictatedWith: 'Diktiert ({model})',
  identifyingFound: 'Das könnte jemanden identifizieren. Formulieren Sie es um oder bestätigen Sie, dass es niemanden identifiziert:',
  identifyingConfirm: 'Identifiziert niemanden',
  identifyingSelect: 'Im Text markieren: {kind}',
  identifying_date: 'ein Datum',
  identifying_number: 'eine lange Nummer',
  identifying_name: 'ein Name',
  identifying_birth: 'eine Geburt',
  imageComments: 'Kommentare',
  commentTool: 'Kommentar an einem Punkt',
  commentToolHelp: 'Klicken Sie auf die Stelle im Bild, zu der der Kommentar gehört',
  commentOnImage: 'Kommentar zum Bild',
  commentWholeImage: 'ganzes Bild',
  commentAtPoint: 'an Pin {number}',
  commentSave: 'Kommentar speichern',
  commentEdit: 'Bearbeiten',
  commentNone: 'Keine Kommentare zu diesem Bild.',
  commentLimit: 'Höchstens {count} Kommentare zu einem Bild.',
  change_commented: '{count} Kommentare hinzugefügt',
  change_commented_one: 'ein Kommentar hinzugefügt',
  change_comment_changed: '{count} Kommentare geändert',
  change_comment_changed_one: 'ein Kommentar geändert',
  change_uncommented: '{count} Kommentare entfernt',
  change_uncommented_one: 'ein Kommentar entfernt',
  commentsHint: 'Ihre Kommentare gehören zu Ihren Annotationen: Sie sind dort zu sehen, wo diese es sind.',
  studyReport: 'Befund',
  studyReportHint: 'Ihr Befund zu dieser Studie, einer für alle Ansichten; er wird beim Schreiben gespeichert und ist dort zu sehen, wo Ihre Annotationen es sind.',
  studyReportEmpty: 'Ein leerer Befund wird entfernt.',
  studyReportChecking: 'Nicht gespeichert, bis die markierten Wörter umformuliert oder bestätigt sind.',
  readerReport: 'Befund des Lesers',
  clinicalReports: 'Klinischer Befund',
  clinicalReportsHint: 'Der Befund, den diese Studie im Krankenhaus erhielt, vor dem Hochladen de-identifiziert. Er ist kein Befund eines Lesers und wird nicht bearbeitet.',
  clinicalReportViews: 'Zu {views}',
  clinicalReportCumulative: 'Umfasst mehrere Untersuchungen',
  clinicalReportIndication: 'Indikation',
  clinicalReportQuestion: 'Fragestellung',
  clinicalReportFindings: 'Befund',
  clinicalReportConclusion: 'Beurteilung',
  clinicalReportNoFindings: 'Ohne Abschnitt „Befund“.',
  clinicalReportContextOnly: 'Ihr Auftrag zeigt nur die klinischen Angaben: Befund und Beurteilung folgen, sobald Sie Ihr Paket abschließen.',
  deleteClinicalReport: 'Klinischen Befund löschen',
  deleteClinicalReportWarning: 'Etwa wenn er einen Patienten identifiziert. Das lässt sich nicht rückgängig machen.',
  dockLabel: 'Diktat und Ansichten',
  dockStudy: 'Studie',
  dockReportDictate: 'Befund der Studie diktieren',
  dockReportAdd: 'Weiter am Befund der Studie diktieren',
  dockReportScope: 'Ein Befund für alle {count} Bilder der Studie',
  dockReportFinished: 'Der Befund kann sich nicht ändern, solange die aktive Ansicht abgeschlossen ist',
  dockCommentDictate: 'Kommentar zu {view} diktieren',
  dockCommentAdd: 'Weiter am Kommentar zu {view} diktieren',
  dockCommentScope: 'Nur dieses Bild: sein Kommentar zum ganzen Bild, im Seitenbereich gespeichert',
  dockFinished: '{view} ist in diesem Auftrag abgeschlossen',
  dockHold: 'Klicken oder {key} halten',
  dockArm: 'Klicken; danach spricht {key} hierhin',
  dockClick: 'Zum Starten und Beenden klicken',
  dockListening: 'Hört zu: Klicken beendet, Esc verwirft die Phrase',
  dockViews: 'Bilder {from}–{to} von {total} angezeigt',
  dockView: 'Bild {from} von {total} angezeigt',
  dockFinishFirst: 'Erst das Diktat des Kommentars beenden',
  dockAddedReport: 'In den Befund der Studie übernommen.',
  dockAddedComment: 'In den Kommentar zu {view} übernommen: im Seitenbereich speichern.',
  dockDropped: 'Sein Feld wurde geschlossen, daher wurde der Text verworfen.',
  speechBench: 'Diktat-Benchmark',
  speechBenchIntro:
    'Lesen Sie die erfundenen Texte nacheinander laut vor, wie Sie sie diktieren würden. Jeder wird mit dem aufgenommen, was das Live-Diktat daraus gemacht hat, für den Sprach-Benchmark. Lesen Sie nur diese Texte, nie etwas über echte Patienten.',
  speechBenchRecordingOff: 'Aufnahmen sind im Sprachdienst nicht eingerichtet: Sie können die Texte ausprobieren, aber nichts wird behalten.',
  speechSpeaker: 'Sprecher-Code',
  speechSpeakerHint: 'Buchstaben und Ziffern, bis zu 16, etwa A1. Nie ein Name.',
  speechTextNumber: 'Text {number} von {total}',
  speechText: 'Text',
  speechTerms: 'Begriffe',
  speechRecord: 'Aufnehmen',
  speechTry: 'Ausprobieren',
  speechStop: 'Stopp',
  speechRecordHelp: 'Klick startet und beendet, oder {key} halten',
  speechLive: 'Live',
  speechFinal: 'Endgültig',
  speechTimes: '{seconds} s gesprochen, endgültiger Text {final} s nach dem Loslassen',
  speechCorrections: 'Glossar-Korrekturen',
  speechEngine: 'Sprachmodell',
  speechFinalPass: 'Zweiter Durchgang',
  speechGlossary: 'Glossar',
  speechRecordings: 'Aufnahmen',
  speechRecordingsNone: 'Noch keine Aufnahmen.',
  speechRecorded: 'Aufgenommen',
  speechSeconds: 'Sekunden',
  speechRecordedAlready: 'von diesem Sprecher aufgenommen',
}
